import { useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { applyDesktopAppUpdate } from '@/utils/applyDesktopAppUpdate'
import {
  APP_UPDATE_RELOAD_KEY,
  planAppUpdateReload,
} from '@/utils/appUpdateReload'

const CHECK_INTERVAL_MS = 5 * 60 * 1000

function readMark(storage: Storage): number | null {
  const raw = storage.getItem(APP_UPDATE_RELOAD_KEY)
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

function writeMark(storage: Storage, markedAt: number | null) {
  if (markedAt === null) storage.removeItem(APP_UPDATE_RELOAD_KEY)
  else storage.setItem(APP_UPDATE_RELOAD_KEY, String(markedAt))
}

/** Busca a versão nova e recarrega uma vez, sem modal. */
export function PwaUpdateNotifier() {
  const hadController = useRef(
    typeof navigator !== 'undefined'
      && 'serviceWorker' in navigator
      && !!navigator.serviceWorker.controller,
  )
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisterError(error) {
      if (import.meta.env.DEV) {
        console.debug('PWA registration skipped:', error.message)
      }
    },
  })
  const updateRef = useRef(updateServiceWorker)
  updateRef.current = updateServiceWorker

  const tryReload = (updateReady: boolean) => {
    if (typeof sessionStorage === 'undefined') return
    const decision = planAppUpdateReload({
      hadController: hadController.current,
      updateReady,
      markedAt: readMark(sessionStorage),
      now: Date.now(),
    })
    writeMark(sessionStorage, decision.markedAt)
    if (!decision.reload) return
    void applyDesktopAppUpdate(updateRef.current)
  }

  useEffect(() => {
    tryReload(false)
  }, [])

  useEffect(() => {
    if (!needRefresh) return
    tryReload(true)
  }, [needRefresh])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onChange = () => tryReload(true)
    navigator.serviceWorker.addEventListener('controllerchange', onChange)
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onChange)
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const checkForUpdate = () => {
      void navigator.serviceWorker.getRegistration().then((registration) => registration?.update())
    }

    checkForUpdate()
    const interval = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS)
    const onFocus = () => checkForUpdate()
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return null
}
